/**
 * Aufgaben — der Schreibtisch für die Bürositzung.
 *
 * Ganz oben „Kommende Termine" (Projekt oder Aufgabe in den nächsten 7 Tagen dran),
 * darunter „Überfällig" — beide offen, bis man sie zuklappt; dann die Themen Geld ·
 * Angebote & Kunden · Planung · Bewertungen · Notizen, zugeklappt mit der Zahl ihrer
 * offenen Punkte (Benjamin, 12.10.2026: „Das Wichtigste sind immer die kommenden
 * Termine"). Wahlweise wie früher nach Kunde gruppiert (22.09.2026). Lage und Thema
 * rechnet `aufgaben_ordnung.py`, gruppiert wird in `lib/aufgabenOrdnung.ts` — die Seite
 * entscheidet nichts selbst. Beim Suchen ist alles aufgeklappt. Aufgeklappt eine Liste,
 * keine Kacheln („Listen sind nicht so erschlagend"). Oben der
 * Filter nach Zuständigkeit: meine, die des anderen Inhabers, alle (Benjamin,
 * 22.09.2026). Bewusst sparsam: keine Adressen und Nummern auf der Karte —
 * die Knöpfe „Mail schreiben" und „Anrufen" kennen sie.
 *
 * Konzept: docs/konzepte/KONZEPT-NOTIZEN-AUFGABEN.md
 */
import { useMemo, useState } from "react";
import { fehlerText } from "@vimaana/ui";

import KundeBlatt from "../components/kunden/KundeBlatt";
import BewertungNachholen from "../components/notizen/BewertungNachholen";
import NotizErfassen from "../components/notizen/NotizErfassen";
import AufgabenGruppen from "../components/notizen/AufgabenGruppen";
import { useBewertungNachholen } from "../hooks/useBewertung";
import { useNotizen, usePersonen } from "../hooks/useNotizen";
import { ordnen, type Gruppe, type Ordnungsart } from "../lib/aufgabenOrdnung";
import { vorname, type Notiz } from "../lib/notizen";
import "../styles/projekte.css";
import "../styles/notizen.css";
import "../styles/bewertung.css";

const ORDNUNG_KEY = "kontor.aufgaben.ordnung";
/** Die Gruppen oben sind offen, bis man sie zuklappt — welche zu sind, merkt sich der Browser. */
const ZU_KEY = "kontor.aufgaben.zu";

function gemerktZu(): Set<string> {
  try {
    const roh = JSON.parse(localStorage.getItem(ZU_KEY) || "[]");
    return new Set(Array.isArray(roh) ? roh.filter((x): x is string => typeof x === "string") : []);
  } catch { return new Set(); }
}

function gemerkteOrdnung(): Ordnungsart {
  try { return localStorage.getItem(ORDNUNG_KEY) === "kunde" ? "kunde" : "thema"; } catch { return "thema"; }
}

function suchtext(n: Notiz): string {
  const k = n.kontext;
  return [n.text, ...n.punkte.map((p) => p.text), k.kunde?.name, k.projekt?.titel, k.lieferant?.name,
    k.anfrage?.name, k.anfrage?.betreff].filter(Boolean).join(" ").toLowerCase();
}

export default function AufgabenPage() {
  const personen = usePersonen().data || [];
  const [wer, setWer] = useState("meine");
  // Drei Ansichten: offen, später (zurückgestellt, Migration 213) und erledigt.
  const [ansicht, setAnsicht] = useState<"offen" | "zurueckgestellt" | "fertig">("offen");
  const erledigt = ansicht === "fertig";
  const spaeter = ansicht === "zurueckgestellt";
  const [q, setQ] = useState("");
  // Themen und Kunden sind zu, bis man sie aufklappt; die Gruppen oben offen, bis man sie zuklappt.
  const [aufgeklappt, setAufgeklappt] = useState<Set<string>>(() => new Set());
  const [zugeklappt, setZugeklappt] = useState<Set<string>>(gemerktZu);
  const suche = q.trim() !== "";
  const offen = (g: Gruppe) => suche || (g.lage ? !zugeklappt.has(g.schluessel) : aufgeklappt.has(g.schluessel));
  const umschalten = (g: Gruppe) => {
    const kippen = (vorher: Set<string>) => {
      const neu = new Set(vorher);
      if (neu.has(g.schluessel)) neu.delete(g.schluessel); else neu.add(g.schluessel);
      return neu;
    };
    if (!g.lage) { setAufgeklappt(kippen); return; }
    const neu = kippen(zugeklappt);
    setZugeklappt(neu);
    try { localStorage.setItem(ZU_KEY, JSON.stringify([...neu])); } catch { /* privates Fenster: dann eben nur für jetzt */ }
  };
  /** Kundendatensatz als Blatt von rechts. */
  const [kundeBlatt, setKundeBlatt] = useState<string | null>(null);
  const { data, isLoading, error } = useNotizen(ansicht, wer);
  // Abgeschlossene Projekte der letzten 90 Tage ohne Bewertungsbitte (Migration 192).
  const nachholen = useBewertungNachholen().data?.projekte.length || 0;
  const [nachholenOffen, setNachholenOffen] = useState(false);

  const filter = [
    { id: "meine", label: "Meine" },
    ...personen.filter((p) => !p.ich).map((p) => ({ id: String(p.id), label: vorname(p.name) })),
    { id: "alle", label: "Alle" },
  ];

  // Nach Thema (Standard, 12.10.2026) oder wie früher nach Kunde; die Wahl merkt sich der Browser.
  const [art, setArt] = useState<Ordnungsart>(gemerkteOrdnung);
  const artWaehlen = (a: Ordnungsart) => {
    setArt(a);
    try { localStorage.setItem(ORDNUNG_KEY, a); } catch { /* privates Fenster: dann eben nur für jetzt */ }
  };
  const gruppen = useMemo(() => {
    const s = q.trim().toLowerCase();
    const treffer = (data || []).filter((n) => !s || suchtext(n).includes(s));
    return ordnen(treffer, art, ansicht === "offen" && !s);
  }, [data, q, art, ansicht]);
  const zaehle = (g: Gruppe) => {
    const punkte = g.notizen.reduce((n, x) => n + x.punkte.filter((p) => !p.erledigt_am).length, 0);
    return erledigt ? g.notizen.length : punkte || g.notizen.length;
  };

  const offenePunkte = (data || []).reduce((n, x) => n + x.punkte.filter((p) => !p.erledigt_am).length, 0);

  /* Telefon (13.10.2026): Überschrift weg, Erfassen · Suche · Filter in EINER Zeile,
     darunter nur „Meine | Volker | Alle". Ordnen und Später/Erledigt liegen im
     Filterblatt — vorher standen vier Zeilen über der ersten Aufgabe. */
  const [suchOffen, setSuchOffen] = useState(false);
  const [filterOffen, setFilterOffen] = useState(false);
  const sucheSchliessen = () => { setQ(""); setSuchOffen(false); };
  const abweichend = ansicht !== "offen" || art !== "thema";
  const ANSICHTEN = [
    { id: "offen", label: "Offen" },
    { id: "zurueckgestellt", label: "Später" },
    { id: "fertig", label: "Erledigt" },
  ] as const;

  return (
    <div className="projekte-page nz-seite">
      <header className="nz-seitenkopf">
        <h1>Aufgaben</h1>
        {ansicht === "offen" && data && offenePunkte > 0 && <span className="nz-zahl">{offenePunkte}</span>}
      </header>
      <div className="nz-kopfzeile">
        <div className="nz-kopf-feld">
          {suchOffen ? (
            <input
              className="nz-feld nz-suche-tel"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") sucheSchliessen(); }}
              placeholder="Aufgaben durchsuchen"
              aria-label="Aufgaben durchsuchen"
              autoFocus
            />
          ) : (
            <NotizErfassen eingeklappt />
          )}
        </div>
        <button
          type="button"
          className={"nz-symbolknopf nz-tel-nur" + (suchOffen ? " is-aktiv" : "")}
          aria-label={suchOffen ? "Suche schließen" : "Suchen"}
          onClick={() => (suchOffen ? sucheSchliessen() : setSuchOffen(true))}
        >
          {suchOffen ? (
            <span aria-hidden="true">✕</span>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
            </svg>
          )}
        </button>
        <button type="button" className="nz-symbolknopf nz-tel-nur" aria-label={abweichend ? "Filter (aktiv)" : "Filter"} onClick={() => setFilterOffen(true)}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          {abweichend && <i className="nz-filterpunkt" aria-hidden="true" />}
        </button>
      </div>
      {nachholenOffen && <BewertungNachholen onClose={() => setNachholenOffen(false)} />}
      <div className="nz-werkzeug">
        {filter.length > 2 && (
          <div className="nz-segmente" role="group" aria-label="Wessen Aufgaben">
            {filter.map((f) => (
              <button key={f.id} type="button" className={"nz-seg" + (wer === f.id ? " is-aktiv" : "")} aria-pressed={wer === f.id} onClick={() => setWer(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
        )}
        <div className="nz-segmente nz-desk-nur" role="group" aria-label="Ordnen nach">
          {(["thema", "kunde"] as const).map((a) => (
            <button key={a} type="button" className={"nz-seg" + (art === a ? " is-aktiv" : "")} aria-pressed={art === a} onClick={() => artWaehlen(a)}>
              {a === "thema" ? "Themen" : "Kunden"}
            </button>
          ))}
        </div>
        <span className="nz-werkzeug-rechts nz-desk-nur">
          <input className="nz-feld nz-suche" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen" aria-label="Aufgaben durchsuchen" />
          <button type="button" className={"nz-seg nz-seg-einzeln" + (spaeter ? " is-aktiv" : "")} aria-pressed={spaeter} onClick={() => setAnsicht(spaeter ? "offen" : "zurueckgestellt")}>
            Später
          </button>
          <button type="button" className={"nz-seg nz-seg-einzeln" + (erledigt ? " is-aktiv" : "")} aria-pressed={erledigt} onClick={() => setAnsicht(erledigt ? "offen" : "fertig")}>
            Erledigt
          </button>
        </span>
        {/* Telefon: was vom Üblichen abweicht, steht als Kärtchen da — sonst wundert
            man sich über eine Liste voller erledigter Aufgaben. */}
        {ansicht !== "offen" && (
          <button type="button" className="nz-filterchip nz-tel-nur" onClick={() => setAnsicht("offen")} aria-label={`${spaeter ? "Später" : "Erledigt"} — zurück zu den offenen`}>
            {spaeter ? "Später" : "Erledigt"} <span aria-hidden="true">✕</span>
          </button>
        )}
      </div>
      {filterOffen && (
        <div className="kontor-sheet-backdrop" onClick={() => setFilterOffen(false)}>
          <div className="kontor-sheet nz-filterblatt" role="dialog" aria-modal="true" aria-label="Aufgaben filtern" onClick={(e) => e.stopPropagation()}>
            <div className="kontor-sheet-griff" aria-hidden="true" />
            <div className="nz-filter-gruppe">
              <span className="nz-filter-titel">Ordnen nach</span>
              <div className="nz-segmente" role="group" aria-label="Ordnen nach">
                {(["thema", "kunde"] as const).map((a) => (
                  <button key={a} type="button" className={"nz-seg" + (art === a ? " is-aktiv" : "")} aria-pressed={art === a} onClick={() => artWaehlen(a)}>
                    {a === "thema" ? "Themen" : "Kunden"}
                  </button>
                ))}
              </div>
            </div>
            <div className="nz-filter-gruppe">
              <span className="nz-filter-titel">Zeigen</span>
              <div className="nz-segmente" role="group" aria-label="Welche Aufgaben">
                {ANSICHTEN.map((v) => (
                  <button key={v.id} type="button" className={"nz-seg" + (ansicht === v.id ? " is-aktiv" : "")} aria-pressed={ansicht === v.id} onClick={() => setAnsicht(v.id)}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" className="kontor-sheet-close" onClick={() => setFilterOffen(false)}>Fertig</button>
          </div>
        </div>
      )}
      {isLoading && <p className="nz-leer">Lade …</p>}
      {error && <p className="nz-fehler">{fehlerText(error)}</p>}
      {data && gruppen.length === 0 && (
        <p className="nz-leer">{q.trim() ? "Nichts gefunden." : erledigt ? "Noch nichts erledigt." : spaeter ? "Nichts zurückgestellt." : "Nichts offen."}</p>
      )}

      {gruppen.length > 0 && (
        <AufgabenGruppen gruppen={gruppen} zaehle={zaehle} onKunde={setKundeBlatt} offen={offen} umschalten={umschalten} />
      )}
      {/* Der Bewertungshinweis steht unter der Liste, nicht darüber (13.10.2026):
          zuerst kommen die Aufgaben. */}
      {ansicht === "offen" && nachholen > 0 && (
        <p className="bw-nachholen nz-nachholen">
          <span>
            {nachholen === 1 ? "Ein Kunde" : `${nachholen} Kunden`} mit abgeschlossenem Projekt wurde{nachholen === 1 ? "" : "n"} noch nicht um eine Google-Bewertung gebeten.
          </span>
          <button type="button" className="vg-btn" onClick={() => setNachholenOffen(true)}>Auswählen</button>
        </p>
      )}

      {kundeBlatt && <KundeBlatt kundeId={kundeBlatt} onClose={() => setKundeBlatt(null)} />}
    </div>
  );
}
